import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Compass, ArrowRight } from "@phosphor-icons/react";
import { localGuide } from "@/data/localGuide";
import Seo, { SITE } from "@/components/site/Seo";

// Long-form authority content for "where to stay near Wilton Drive" searches.
export default function LocalGuide() {
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: localGuide.title,
    description: localGuide.intro,
    about: ["Wilton Drive", "Wilton Manors", "Fort Lauderdale", "vacation rentals"],
    author: { "@type": "Organization", name: "Double Rainbow Suites", "@id": `${SITE}/#business` },
    publisher: { "@id": `${SITE}/#business` },
    mainEntityOfPage: `${SITE}/neighborhood`,
    articleSection: localGuide.sections.map((s) => s.h),
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-testid='local-guide']"] },
  };

  return (
    <section data-testid="local-guide" className="max-w-[1000px] mx-auto px-6 md:px-10 py-8 md:py-12">
      <Seo jsonLd={articleLd} />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="overline text-sunset mb-4 flex items-center gap-2">
          <Compass size={16} weight="fill" className="text-sunset" /> {localGuide.eyebrow}
        </p>
        <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mb-6">
          {localGuide.title}
        </h2>
        <p className="text-emerald-soft text-lg md:text-xl leading-relaxed font-light">
          {localGuide.intro}
        </p>
      </motion.div>

      <div className="mt-12 md:mt-16 flex flex-col gap-10 md:gap-12">
        {localGuide.sections.map((s, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 2) * 0.06, ease: [0.16, 1, 0.3, 1] }}
            className="border-t border-emerald/12 pt-8"
            data-testid={`local-guide-section-${i}`}
          >
            <h3 className="font-display text-2xl md:text-3xl leading-snug text-emerald mb-4">{s.h}</h3>
            {s.body.map((p, j) => (
              <p key={j} className="text-emerald-soft leading-relaxed font-light mb-4 last:mb-0">
                {p}
              </p>
            ))}
          </motion.article>
        ))}
      </div>

      <div className="mt-12">
        <Link
          to="/properties"
          data-testid="local-guide-cta"
          className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald"
        >
          See stays near Wilton Drive
          <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
}
